import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ArrowUpDown, SlidersHorizontal, Plus, Trash2, ArrowRight, Star, UtensilsCrossed } from 'lucide-react';
import { Trans, Plural } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { ordersApi } from '@/lib/api/services/orders.api';
import { cn } from '@/lib/utils';
import { MenuItemCard } from './MenuItemCard';
import { CartLines } from './CartLines';
import type { useOrderState } from './useOrderState';

type OrderState = ReturnType<typeof useOrderState>;

interface Props {
  state: OrderState;
  menuId?: number;
  onContinue: () => void;
}

export function Step1SelectItems({ state, menuId, onContinue }: Props) {
  const { t } = useLingui();
  const [search, setSearch] = useState('');
  // undefined  = not yet interacted (auto-selects first category once loaded)
  // null       = "All" explicitly chosen (no category filter)
  // string     = specific category
  const [activeCategory, setActiveCategory] = useState<string | null | undefined>(undefined);

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['order-menu-categories', menuId],
    queryFn: () => ordersApi.getMenuCategories({ menuId }),
  });

  // Auto-select first category on first load; honour explicit null ("All")
  const resolvedCategory = activeCategory === undefined ? (categories[0] ?? null) : activeCategory;

  // Fetch items for current category + search
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['order-menu-items', menuId, resolvedCategory, search],
    queryFn: () =>
      ordersApi.getMenuItems({
        menuId,
        category: resolvedCategory ?? undefined,   // null → no filter
        search: search.trim() || undefined,
      }),
  });

  const featured = useMemo(() => items.find((i) => i.isFeatured), [items]);
  const gridItems = useMemo(
    () => (featured ? items.filter((i) => i.id !== featured.id) : items),
    [items, featured],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_25rem] gap-6 items-start">
      {/* ── Left: Menu browser ── */}
      <div className="space-y-5">
        {/* Category tabs */}
        {categories.length > 0 && (
          <div className="flex items-center gap-2 bg-(--color-surface-muted) p-1 rounded-xl border border-(--color-border) w-fit flex-wrap">
            {/* All tab */}
            <button
              type="button"
              onClick={() => setActiveCategory(null)}
              className={cn(
                'px-3.5 py-2 rounded-[0.55rem] text-[0.84375rem] font-semibold transition-colors',
                resolvedCategory === null
                  ? 'bg-(--color-surface-content) text-(--color-text-primary) shadow-sm'
                  : 'text-(--color-text-secondary) hover:text-(--color-text-primary)',
              )}
            >
              <Trans>All</Trans>
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={cn(
                  'px-3.5 py-2 rounded-[0.55rem] text-[0.84375rem] font-semibold transition-colors',
                  resolvedCategory === cat
                    ? 'bg-(--color-surface-content) text-(--color-text-primary) shadow-sm'
                    : 'text-(--color-text-secondary) hover:text-(--color-text-primary)',
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Filter row */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[16.25rem] flex items-center gap-2 h-11 px-3 bg-(--color-surface-muted) border border-(--color-border) rounded-lg">
            <Search className="w-4 h-4 text-(--color-text-secondary) shrink-0" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t`Search dishes, ingredients…`}
              className="bg-transparent border-0 focus-visible:ring-0 p-0 h-auto text-[0.875rem]"
            />
          </div>
          <Button variant="outline" size="sm" className="h-11 px-4 gap-2">
            <ArrowUpDown className="w-4 h-4" />
            <Trans>Sort</Trans>
          </Button>
          <Button variant="outline" size="sm" className="h-11 px-4 gap-2">
            <SlidersHorizontal className="w-4 h-4" />
            <Trans>Filter</Trans>
          </Button>
        </div>

        {/* Featured hero */}
        {featured && !search && (
          <div className="relative rounded-2xl overflow-hidden border border-(--color-border) h-[13.75rem]">
            {featured.imageUrl && (
              <img
                src={featured.imageUrl}
                alt={featured.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,.78) 100%)',
              }}
            />
            <div className="relative h-full flex flex-col justify-between p-5 text-white">
              <div className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-[0.75rem] font-semibold">
                  <Star className="w-3.5 h-3.5" />
                  <Trans>Highlighted</Trans>
                </span>
              </div>
              <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[1.75rem] font-extrabold leading-tight">{featured.name}</div>
                  {featured.description && (
                    <div className="text-[0.8125rem] text-white/85 max-w-[32.5rem] line-clamp-2">
                      {featured.description}
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={() => state.addItem(featured)}
                  variant="secondary"
                  className="bg-(--color-surface-content) text-(--color-text-primary) hover:bg-white/90 gap-2 shrink-0"
                >
                  <Trans>Add to order</Trans>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Section header */}
        <div className="flex items-end justify-between pt-1">
          <h2 className="text-[1.75rem] font-extrabold tracking-tight">
            <Trans>Menu Items</Trans>
          </h2>
          <span className="text-[0.8125rem] text-(--color-text-secondary)">
            {resolvedCategory ? `${resolvedCategory} · ` : ''}
            <Plural value={gridItems.length} one="# item" other="# items" />
          </span>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-2xl" />
            ))}
          </div>
        ) : gridItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {gridItems.map((item) => (
              <MenuItemCard
                key={item.id}
                item={item}
                inCart={state.isInCart(item.id)}
                onAdd={state.addItem}
              />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center text-(--color-text-secondary) text-[0.875rem]">
            <Trans>No items match your search.</Trans>
          </div>
        )}
      </div>

      {/* ── Right: Current Order panel ── */}
      <aside className="lg:sticky lg:top-3">
        <div className="bg-(--color-surface-muted) border border-(--color-border) rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b border-(--color-border) flex items-center justify-between bg-(--color-surface-content)">
            <div>
              <div className="text-[0.6875rem] tracking-wider text-(--color-text-secondary) font-semibold uppercase">
                <Trans>Current Order</Trans>
              </div>
              <div className="text-[1.125rem] font-bold">
                <Trans>Ticket</Trans>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-(--color-order-cooking-bg) text-(--color-order-cooking-fg) text-[0.6875rem] font-bold">
              <Trans>DRAFT</Trans>
            </span>
          </div>

          {/* Cart lines */}
          <div className="px-3 py-3 max-h-[26.25rem] overflow-y-auto bg-(--color-surface-content)">
            {state.cart.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-(--color-surface-muted) border border-(--color-border) flex items-center justify-center text-(--color-text-secondary) mb-3">
                  <UtensilsCrossed className="w-5 h-5" />
                </div>
                <p className="text-[0.875rem] font-semibold">
                  <Trans>No items yet</Trans>
                </p>
                <p className="text-[0.8125rem] text-(--color-text-secondary) mt-1">
                  <Trans>Tap + on a menu card to add it to the ticket.</Trans>
                </p>
              </div>
            ) : (
              <CartLines lines={state.cart} onSetQty={state.setQty} />
            )}
          </div>

          {/* Totals */}
          <div className="px-5 py-4 border-t border-(--color-border) text-[0.8125rem] space-y-1.5 bg-(--color-surface-content)">
            <div className="flex justify-between">
              <span className="text-(--color-text-secondary)"><Trans>Subtotal</Trans></span>
              <span className="font-semibold">${state.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-(--color-text-secondary)"><Trans>Tax (8.25%)</Trans></span>
              <span className="font-semibold">${state.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-[1rem] pt-2 mt-1 border-t border-(--color-border)">
              <span className="font-bold"><Trans>Total</Trans></span>
              <span className="font-extrabold">${state.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="p-3 bg-(--color-surface-content) border-t border-(--color-border) flex items-center gap-2">
            <Button
              type="button"
              variant="destructive-subtle"
              size="icon"
              onClick={state.clearCart}
              title={t`Clear cart`}
              className="h-11 w-11 shrink-0"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              onClick={onContinue}
              disabled={state.cart.length === 0}
              className="flex-1 h-11 gap-2"
            >
              <Trans>Continue</Trans>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
}
